import { PRIVILEGES } from 'constants/privileges';
import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useAuth, setGlobalTransportCloudLeave } from 'contexts/authContext';
import { useTransportCloudContext } from 'contexts/transportCloudContext';
import { useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import SockJS from 'sockjs-client';
import { hasPrivilege } from 'utils/authFunctions.utils';
import webstomp from 'webstomp-client';
import { TransportCloudTable } from './TransportCloudTable';

export const TransportCloudModule = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    setWebSocketClient,
    notifyCellUpdate,
    setActiveUsers,
    sendLeaveMessage,
    setLockedCells,
    callCleanupEditing,
    setIsConnected,
  } = useTransportCloudContext();

  const sendLeaveMessageRef = useRef(sendLeaveMessage);
  const cleanupEditingRef = useRef(callCleanupEditing);
  const activeClientRef = useRef<any>(null);

  useEffect(() => {
    sendLeaveMessageRef.current = sendLeaveMessage;
    cleanupEditingRef.current = callCleanupEditing;
  }, [sendLeaveMessage, callCleanupEditing]);

  useEffect(() => {
    setGlobalTransportCloudLeave(sendLeaveMessage);
    return () => {
      setGlobalTransportCloudLeave(null);
    };
  }, [sendLeaveMessage]);

  if (user?.clientPanelUser) {
    navigate(reactPaths.home);
  }

  useEffect(() => {
    const doConnect = () => {
      const socket = new SockJS('backend/service/ws-transport-cloud');
      const client = webstomp.over(socket);
      activeClientRef.current = client;

      client.connect(
        {
          'accept-version': '1.2',
          'heart-beat': '10000,10000',
        },
        () => {
          setWebSocketClient(client);
          setIsConnected(true);

          client.subscribe('/topic/transportCloud/updates', (message) => {
            try {
              const data = JSON.parse(message.body);

              if (data.type === 'CELL_UPDATED' || data.type === 'CELL_ERROR') {
                notifyCellUpdate({
                  type: data.type,
                  rowId: data.rowId,
                  fieldName: data.fieldName,
                  newValue: data.newValue,
                  error: data.message || data.error || data.errorCode,
                });
              } else if (data.type === 'CELL_UPDATED_NOTIFY') {
                notifyCellUpdate({
                  type: data.type,
                  rowId: data.rowId,
                  fieldName: data.fieldName,
                  newValue: data.newValue,
                });
              } else if (data.type === 'ROWS_DELETED') {
                notifyCellUpdate({
                  type: data.type,
                  jobEntryIds: data.jobEntryIds,
                  shipmentPlanContainerIds: data.shipmentPlanContainerIds,
                });
              } else if (data.type === 'DELETE_ERROR') {
                notifyCellUpdate({
                  type: data.type,
                  errorCode: data.errorCode,
                  jobEntryIds: data.jobEntryIds,
                  shipmentPlanContainerIds: data.shipmentPlanContainerIds,
                });
              } else if (data.type === 'ACTIVE_USERS') {
                setActiveUsers(data.users || []);
              } else if (data.type === 'CELL_EDIT_STARTED') {
                setLockedCells((prev) => {
                  const exists = prev.some(
                    (cell) =>
                      cell.rowId === data.rowId &&
                      cell.fieldName === data.fieldName,
                  );
                  if (exists) return prev;
                  return [
                    ...prev,
                    {
                      rowId: data.rowId,
                      fieldName: data.fieldName,
                      user: data.user,
                    },
                  ];
                });
              } else if (
                data.type === 'CELL_EDIT_ENDED' ||
                data.type === 'CELL_EDIT_STOPPED'
              ) {
                setLockedCells((prev) =>
                  prev.filter(
                    (cell) =>
                      !(
                        cell.rowId === data.rowId &&
                        cell.fieldName === data.fieldName
                      ),
                  ),
                );
              }
            } catch (error) {}
          });

          client.send('/app/transport-cloud/join', JSON.stringify({}));
        },
        () => {
          if (activeClientRef.current === client) {
            setWebSocketClient(null);
            setIsConnected(false);
          }
        },
      );
    };

    const handleBeforeUnload = () => {
      const client = activeClientRef.current;
      if (client && client.connected) {
        try {
          cleanupEditingRef.current();
          sendLeaveMessageRef.current();
          if (navigator.sendBeacon && user?.id) {
            navigator.sendBeacon(
              '/backend/service/transport-cloud/leave',
              JSON.stringify({ userId: user.id }),
            );
          }
          client.disconnect(() => {});
        } catch (error) {}
      }
    };

    const handleVisibilityChange = () => {
      const client = activeClientRef.current;
      if (document.visibilityState === 'hidden') {
        if (client && client.connected) {
          try {
            cleanupEditingRef.current();
            sendLeaveMessageRef.current();
          } catch (error) {}
        }
      } else if (document.visibilityState === 'visible') {
        if (client && client.connected) {
          try {
            client.send('/app/transport-cloud/join', JSON.stringify({}));
          } catch (error) {}
        } else {
          doConnect();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    doConnect();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      const client = activeClientRef.current;
      if (client && client.connected) {
        try {
          cleanupEditingRef.current();
          sendLeaveMessageRef.current();
        } catch (error) {}

        setWebSocketClient(null);
        setIsConnected(false);
        client.disconnect(() => {});
      }
      activeClientRef.current = null;
    };
  }, []);

  return (
    <Box
      alignItems='center'
      display='flex'
      flexDirection='column'
      sx={{
        maxHeight: '100%',
        overflow: 'hidden',
        flex: '1 1 auto',
        width: '100%',
      }}
    >
      {hasPrivilege(
        PRIVILEGES.TransportCloud.VIEW_TRANSPORT_CLOUD_LIST,
        user,
      ) && <TransportCloudTable />}
    </Box>
  );
};
